import { groupBy, find } from "lodash-es";

import {
  IWithballData,
  IGroupedWithBallSummary,
  IWithBallSummary,
  IMiniUser,
  IPassMatrixData,
  IGroupedWithBallByPosition,
  ICoordinate,
} from "types";
import {
  SHOOT_METHOD,
  SHOOT_BODY_PART,
  PASS_METHOD,
  PASS_DIRECTION,
  EVENT_VALUES,
} from "modules/withball-data-input/constants";

export const summariseWithBallTeamReport = (
  rawDataArray: IWithballData[]
): IGroupedWithBallSummary | null => {
  if (rawDataArray.length <= 0) {
    return null;
  }

  const result: IGroupedWithBallSummary = {};
  rawDataArray.map((item) => {
    if (result[item.action] !== undefined) {
      result[item.action].total = result[item.action].total + 1;
    } else {
      result[item.action] = {
        total: 1,
        success: 0,
        fail: 0,
        threat: 0,
      };
    }

    if (item.succeed) {
      result[item.action].success = result[item.action].success + 1;
    } else {
      result[item.action].fail = result[item.action].fail + 1;
    }

    if (item.threat) {
      result[item.action].threat = result[item.action].threat + 1;
    }
    return null;
  });
  return result;
};

export const getActionSummaryFromReport = (
  report: IGroupedWithBallSummary | null,
  action: number
): IWithBallSummary => {
  const emptySummary = {
    total: 0,
    success: 0,
    fail: 0,
    threat: 0,
  };
  if (!report) {
    return emptySummary;
  }

  if (!report[action]) {
    return emptySummary;
  }

  return report[action];
};

export const getUsersFromWithballRawData = (
  rawArray: IWithballData[],
  excludeUnknownPlayer: boolean = false
): IMiniUser[] => {
  return rawArray.reduce((accumulator: IMiniUser[], rawData) => {
    if (!excludeUnknownPlayer || rawData.fromPlayer !== -1) {
      if (!accumulator.find((each) => each.id === rawData.fromPlayer)) {
        accumulator.push({
          id: rawData.fromPlayer,
          name: rawData.fromName,
        });
      }
    }

    if (!excludeUnknownPlayer || rawData.toPlayer !== -1) {
      if (!accumulator.find((each) => each.id === rawData.toPlayer)) {
        accumulator.push({
          id: rawData.toPlayer,
          name: rawData.toName,
        });
      }
    }

    return accumulator;
  }, []);
};

export const groupWithBallDataByActionType = (
  rawDataArray: IWithballData[]
): Record<number, IWithballData[]> => {
  return groupBy(rawDataArray, (data) => {
    return data.action;
  });
};

export const buildMatrixFromPassData = (rawArray: IWithballData[]) => {
  const matrix: any = {};
  rawArray.map((data) => {
    if (
      !data.toPlayer ||
      data.toPlayer < 0 ||
      data.fromPlayer === data.toPlayer
    ) {
      return null;
    }

    if (!matrix[data.fromPlayer]) {
      matrix[data.fromPlayer] = [];
    }

    const dataFromPlayer: IPassMatrixData[] = matrix[data.fromPlayer];
    const dataToPlayer = find(dataFromPlayer, (each) => {
      return each.toPlayer === data.toPlayer;
    });

    if (dataToPlayer) {
      dataToPlayer.total++;
      if (data.succeed) {
        dataToPlayer.succ++;
      }
      return null;
    }

    dataFromPlayer.push({
      fromPlayer: data.fromPlayer,
      fromName: data.fromName,
      toPlayer: data.toPlayer,
      toName: data.toName,
      total: 1,
      succ: data.succeed ? 1 : 0,
    });
    return null;
  });
  return matrix;
};

export const groupWithBallByPosition = (
  rawDataArray: IWithballData[]
): IGroupedWithBallByPosition => {
  return {
    selfHalf: rawDataArray.filter((data) => data.fromX < 0.5),
    otherHalf: rawDataArray.filter((data) => data.fromX >= 0.5),
  };
};

export const groupWithBallByFromPlayer = (rawDataArray: IWithballData[]) => {
  return groupBy(rawDataArray, (data) => data.fromPlayer);
};

export const calculatePecentage = (
  dividend: number,
  divisor: number,
  decimal: number = 1
): string => {
  if (divisor === 0) {
    return "0.0%";
  }
  return `${((dividend / divisor) * 100).toFixed(decimal)}%`;
};

export const calculateDivision = (
  dividend: number,
  divisor: number,
  decimal: number = 1
): number => {
  if (divisor === 0) {
    return 0;
  }
  // return `${((dividend / divisor) * 100).toFixed(decimal)}%`;
  // return (dividend / divisor).toPrecision(decimal);
  return (
    Math.round((dividend / divisor) * Math.pow(10, decimal)) /
    Math.pow(10, decimal)
  );
};

export const summariseShoot = (
  rawDataArray: IWithballData[],
  includeFeet = false
) => {
  const inPenaltyShoot = rawDataArray.filter(
    (rawData) => rawData.method === SHOOT_METHOD.insidePenalty
  );
  const outPenaltyShoot = rawDataArray.filter(
    (rawData) => rawData.method === SHOOT_METHOD.outsidePenalty
  );

  let all = {
    total: rawDataArray.length,
    inTarget: rawDataArray.filter((rawData) => rawData.threat).length,
    goal: rawDataArray.filter((rawData) => rawData.succeed).length,
  };
  let inPenalty = {
    total: inPenaltyShoot.length,
    inTarget: inPenaltyShoot.filter((rawData) => rawData.threat).length,
    goal: inPenaltyShoot.filter((rawData) => rawData.succeed).length,
  };
  let outPenalty = {
    total: outPenaltyShoot.length,
    inTarget: outPenaltyShoot.filter((rawData) => rawData.threat).length,
    goal: outPenaltyShoot.filter((rawData) => rawData.succeed).length,
  };
  let feet = includeFeet
    ? {
        left: {
          total: rawDataArray.filter(
            (data) => data.bodyPart === SHOOT_BODY_PART.leftFoot
          ).length,
          goal: rawDataArray.filter(
            (data) => data.bodyPart === SHOOT_BODY_PART.leftFoot && data.succeed
          ).length,
        },
        right: {
          total: rawDataArray.filter(
            (data) => data.bodyPart === SHOOT_BODY_PART.rightFoot
          ).length,
          goal: rawDataArray.filter(
            (data) =>
              data.bodyPart === SHOOT_BODY_PART.rightFoot && data.succeed
          ).length,
        },
        header: {
          total: rawDataArray.filter(
            (data) => data.bodyPart === SHOOT_BODY_PART.head
          ).length,
          goal: rawDataArray.filter(
            (data) => data.bodyPart === SHOOT_BODY_PART.head && data.succeed
          ).length,
        },
      }
    : {};
  return {
    all,
    inPenalty,
    outPenalty,
    ...feet,
  };
};

export const buildMatrixFromShootData = (rawDataArray: IWithballData[]) => {
  const shoots = groupBy(rawDataArray, (data) => {
    return data.fromPlayer;
  });

  // filter out the data that toPlayer === fromPlayer
  const filteredArray = rawDataArray.filter(
    (data) => data.fromPlayer !== data.toPlayer && data.toPlayer >= 0
  );
  const assists = groupBy(filteredArray, (data) => {
    return data.toPlayer;
  });
  return {
    shoots,
    assists,
  };
};

export const groupPossessionStrive = (rawDataArray: IWithballData[]) => {
  const posWinData = rawDataArray.filter((data) => data.possession === "win");
  const posLoseData = rawDataArray.filter((data) => data.possession === "lose");
  return {
    win: {
      total: posWinData.length,
      data: posWinData,
      ...groupWithBallByPosition(posWinData),
    },
    lose: {
      total: posLoseData.length,
      data: posLoseData,
      ...groupWithBallByPosition(posLoseData),
    },
  };
};

export const groupPersonalData = (
  playerId: string,
  rawDataArray: IWithballData[],
  includesActionTo: boolean = false
) => {
  const filteredData = rawDataArray.filter((data) => {
    if (includesActionTo) {
      return (
        data.fromPlayer.toString() === playerId ||
        data.toPlayer.toString() === playerId
      );
    }
    return data.fromPlayer.toString() === playerId;
  });
  return groupBy(filteredData, (data) => {
    return data.action;
  });
};

export const filterMyValidData = (
  playerId: string,
  myGroupedRawData: { [key: string]: IWithballData[] }
): IWithballData[] => {
  const myData: IWithballData[] = [];

  Object.getOwnPropertyNames(myGroupedRawData).map((action) => {
    myGroupedRawData[action].map((rawData) => {
      switch (rawData.action) {
        case EVENT_VALUES.PASS:
        case EVENT_VALUES.THROUGH:
          if (rawData.fromPlayer.toString() === playerId) {
            myData.push(rawData);
          }
          if (rawData.succeed && rawData.toPlayer.toString() === playerId) {
            myData.push(rawData);
          }
          return null;
        case EVENT_VALUES.CORNER_KICK:
        case EVENT_VALUES.FREE_KICK:
        case EVENT_VALUES.CLEARANCE:
        case EVENT_VALUES.DRIBBLE:
        case EVENT_VALUES.TACKLE:
        case EVENT_VALUES.INTERCEPTION:
        case EVENT_VALUES.SHOOT:
        case EVENT_VALUES.PENALTY:
        case EVENT_VALUES.BLOCK:
          if (rawData.fromPlayer.toString() === playerId) {
            myData.push(rawData);
          }
          return null;
        default:
          return null;
      }
    });
    return null;
  });
  return myData;
};

export const personalData = (
  playerId: string,
  rawDataArray: IWithballData[],
  bothFromAndTo: boolean = false
) => {
  return rawDataArray.filter((data) => {
    if (bothFromAndTo) {
      return (
        data.fromPlayer.toString() === playerId ||
        data.toPlayer.toString() === playerId
      );
    }
    return data.fromPlayer.toString() === playerId;
  });
};

export const calcPassDirection = (pass: IWithballData) => {
  if (pass.fromX < 0 || pass.fromY < 0 || pass.toX < 0 || pass.toY < 0) {
    return PASS_DIRECTION.unknown;
  }

  const angle = Math.atan2(pass.toY - pass.fromY, pass.toX - pass.fromX);
  const absAngle = Math.abs(angle);
  if (absAngle < Math.PI / 4) {
    return PASS_DIRECTION.forward;
  } else if (absAngle < (Math.PI / 4) * 3) {
    return PASS_DIRECTION.cross;
  }
  return PASS_DIRECTION.backward;
};

export const summarisePass = (rawDataArray: IWithballData[]) => {
  let total = 0;
  let succ = 0;
  let passForward = 0;
  let passForwardSucc = 0;
  let passBackward = 0;
  let passBackwardSucc = 0;
  let passCross = 0;
  let passCrossSucc = 0;
  let short = 0;
  let shortSucc = 0;
  let long = 0;
  let longSucc = 0;
  let throwIn = 0;
  let throwInSucc = 0;
  let selfHalf = 0;
  let selfHalfSucc = 0;
  let otherHalf = 0;
  let otherHalfSucc = 0;
  const passForwardData: IWithballData[] = [];
  const passCrossData: IWithballData[] = [];
  const passBackwardData: IWithballData[] = [];
  const passShortData: IWithballData[] = [];
  const passLongData: IWithballData[] = [];
  const passThrowInData: IWithballData[] = [];
  const selfHalfData: IWithballData[] = [];
  const otherHalfData: IWithballData[] = [];

  rawDataArray.map((data) => {
    total = total + 1;
    if (data.succeed) {
      succ = succ + 1;
    }

    switch (data.method) {
      case PASS_METHOD.throwIn:
        throwIn = throwIn + 1;
        if (data.succeed) {
          throwInSucc = throwInSucc + 1;
        }
        passThrowInData.push(data);
        break;
      case PASS_METHOD.long:
        long = long + 1;
        if (data.succeed) {
          longSucc = longSucc + 1;
        }
        passLongData.push(data);
        break;
      // FIXME: a bug here, method might be 0 for initialize wrong value, but default should be short pass
      default:
        short = short + 1;
        if (data.succeed) {
          shortSucc = shortSucc + 1;
        }
        passShortData.push(data);
    }

    // rest of data depends on valid from & to position
    if (data.fromX < 0 || data.fromY < 0 || data.toX < 0 || data.toY < 0) {
      return null;
    }

    switch (calcPassDirection(data)) {
      case PASS_DIRECTION.forward:
        passForward = passForward + 1;
        if (data.succeed) {
          passForwardSucc = passForwardSucc + 1;
        }
        passForwardData.push(data);
        break;
      case PASS_DIRECTION.backward:
        passBackward = passBackward + 1;
        if (data.succeed) {
          passBackwardSucc = passBackwardSucc + 1;
        }
        passBackwardData.push(data);
        break;
      case PASS_DIRECTION.cross:
        passCross = passCross + 1;
        if (data.succeed) {
          passCrossSucc = passCrossSucc + 1;
        }
        passCrossData.push(data);
    }

    if (data.fromX < 0.5) {
      selfHalf = selfHalf + 1;
      if (data.succeed) {
        selfHalfSucc = selfHalfSucc + 1;
      }
      selfHalfData.push(data);
    } else {
      otherHalf = otherHalf + 1;
      if (data.succeed) {
        otherHalfSucc = otherHalfSucc + 1;
      }
      otherHalfData.push(data);
    }
    return null;
  });

  return {
    total,
    succ,
    passForward: {
      total: passForward,
      succ: passForwardSucc,
      data: passForwardData,
    },
    passBackward: {
      total: passBackward,
      succ: passBackwardSucc,
      data: passBackwardData,
    },
    passCross: {
      total: passCross,
      succ: passCrossSucc,
      data: passCrossData,
    },
    short: {
      total: short,
      succ: shortSucc,
      data: passShortData,
    },
    long: {
      total: long,
      succ: longSucc,
      data: passLongData,
    },
    throwIn: {
      total: throwIn,
      succ: throwInSucc,
      data: passThrowInData,
    },
    selfHalf: {
      total: selfHalf,
      succ: selfHalfSucc,
      data: selfHalfData,
    },
    otherHalf: {
      total: otherHalf,
      succ: otherHalfSucc,
      data: otherHalfData,
    },
  };
};

export const buildPersonalPassMatrix = (
  playerId: string,
  rawDataArray: IWithballData[]
) => {
  const passToOtherData = rawDataArray.filter(
    (data) => data.fromPlayer.toString() === playerId && data.succeed
  );
  const passToOtherMatrix = groupBy(passToOtherData, (data) => data.toPlayer);

  const otherPassToMeData = rawDataArray.filter(
    (data) => data.toPlayer.toString() === playerId && data.succeed
  );
  const otherPassToMeMatrix = groupBy(
    otherPassToMeData,
    (data) => data.fromPlayer
  );

  return {
    passToOtherMatrix,
    otherPassToMeMatrix,
  };
};

export const buildPersonalShootMatrix = (rawDataArray: IWithballData[]) => {
  const passToMeMatrix = groupBy(rawDataArray, (data) => data.toPlayer);
  return {
    passToMeMatrix,
  };
};

export const flipPosition = (
  rawPosition: ICoordinate,
  isHomeTeam: boolean,
  isHomeTeamLeftSide: boolean
): ICoordinate => {
  const outPosition: ICoordinate = {
    x: rawPosition.x,
    y: rawPosition.y,
  };

  if (isHomeTeam) {
    if (!isHomeTeamLeftSide) {
      outPosition.x = 1 - rawPosition.x;
      outPosition.y = 1 - rawPosition.y;
    }
  } else {
    if (isHomeTeamLeftSide) {
      outPosition.x = 1 - rawPosition.x;
      outPosition.y = 1 - rawPosition.y;
    }
  }

  return outPosition;
};

export const groupWithBallByAction = (rawDataArray: IWithballData[]) => {
  return rawDataArray.reduce((accumulator: any, rawData) => {
    const actionStr = rawData.action.toString();
    if (!accumulator[actionStr]) {
      accumulator[actionStr] = [];
    }
    accumulator[actionStr].push(rawData);
    return accumulator;
  }, {});
};

export const groupWithBallDataByPlayer = (
  rawDataArray: IWithballData[],
  includeToData?: boolean
): {
  [key: string]: {
    data: IWithballData[];
    name: string;
  };
} => {
  return rawDataArray.reduce((accumulator: any, rawData) => {
    const fromPlayerStr = rawData.fromPlayer.toString();
    if (!accumulator[fromPlayerStr]) {
      accumulator[fromPlayerStr] = {};
      accumulator[fromPlayerStr].data = [];
      accumulator[fromPlayerStr].name = rawData.fromName;
    }
    accumulator[fromPlayerStr].data.push(rawData);

    if (includeToData && rawData.toPlayer >= 0) {
      const toPlayerStr = rawData.toPlayer.toString();
      if (!accumulator[toPlayerStr]) {
        accumulator[toPlayerStr] = {};
        accumulator[toPlayerStr].data = [];
        accumulator[toPlayerStr].name = rawData.toName;
      }
      accumulator[toPlayerStr].data.push(rawData);
    }
    return accumulator;
  }, {});
};

const FieldWidth = 100;
const FieldHeight = 60;

export const calculateActiveEventDistance = (
  playerId: number,
  rawDataArray: IWithballData[],
  includeFailedEvent: boolean
) => {
  // calculate the distance between the player and the ball when the player touch the ball
  let lastPosition: ICoordinate = { x: 0, y: 0 };
  const distance = rawDataArray.reduce(
    (accumulator: number, rawData, index) => {
      let position: ICoordinate = { x: 0, y: 0 };
      if (
        rawData.fromPlayer === playerId ||
        (rawData.toPlayer === playerId && rawData.succeed)
      ) {
        position = { x: rawData.fromX, y: rawData.fromY };
      } else if (includeFailedEvent && !rawData.succeed) {
        position = { x: rawData.toX, y: rawData.toY };
      }

      if (index === 0) {
        lastPosition = position;
        return accumulator;
      }

      // calculate the distance between the last position and the current position
      const deltaX = Math.abs(position.x - lastPosition.x) * FieldWidth;
      const deltaY = Math.abs(position.y - lastPosition.y) * FieldHeight;
      const delta = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      lastPosition = position;
      accumulator = accumulator + delta;

      return accumulator;
    },
    0
  );
  return parseFloat(distance.toFixed(1));
};

export const getEffectiveEvent = (
  playerId: number,
  rawDataArray: IWithballData[]
): IWithballData[] => {
  const res = rawDataArray.filter((rawData) => {
    if (rawData.fromPlayer === playerId) {
      return true;
    }

    return rawData.succeed;
  });
  return res;
};
